import { AbsoluteFill } from 'remotion'
import ZealousSvg from '../assets/ZealousSvg'
import { FallbackImg } from '../components/FallbackImg'
import { BarsVisualization } from '../components/waveform/BarsVisualization'
import { LayoutInfo, LayoutProps } from '../types'
import { defaultClipScene } from '../types/defaultClipScene'
import { cn } from '../utils/cn'
import { useOrientation } from '../utils/useOrientation'
import { sceneAudioSrc } from '../utils/sceneAudioSrc'

export default function Edgy({
  clipScene,
  children,
  orientation,
  speaker,
  style,
}: LayoutProps) {
  const { p, s, l, lp, sp } = useOrientation(orientation)

  return (
    <AbsoluteFill>
      {orientation === 'landscape' && (
        <div
          className="h-[53px] w-full"
          style={{ background: style.border.color }}
        />
      )}

      <div
        className={cn('flex h-full', l && 'flex-row-reverse', sp && 'flex-col')}
      >
        <div
          style={{ background: style.border.color }}
          className={cn(
            l && 'mb-[50px] flex-1 px-[80px] py-[27px]',
            sp && 'mr-[38px] h-full p-[60px]',
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-[41px]">
              <FallbackImg
                className="aspect-square h-[70px] rounded-full"
                src={speaker?.avatarUrl}
              />
              <p
                style={style.speakerUsername}
                className="text-[40px] leading-[120%] font-[500]"
              >
                {speaker?.username}
              </p>
            </div>

            {clipScene.showZealousLogo && (
              <ZealousSvg
                color={style.logo.color}
                className="h-[49px] opacity-50"
              />
            )}
          </div>

          <div className="flex h-full items-center">{children}</div>
        </div>

        {orientation === 'landscape' ? (
          <div className="flex w-[674px] flex-col justify-between">
            <div className="px-[60px] pt-[77px] text-[40px] leading-[140%] font-[500]">
              <FallbackImg
                className="mb-[50px] aspect-square h-[130px] object-cover"
                src={clipScene.imageUrl}
              />

              <p style={style.title} className="line-clamp-3">
                {clipScene.title}
              </p>

              <p style={style.subtitle} className="line-clamp-1">
                {clipScene.subtitle}
              </p>
            </div>

            <div
              className="h-[286px] border-t-2 p-[50px]"
              style={{ borderColor: style.border.color }}
            >
              <BarsVisualization
                className="h-full"
                color={style.waveform.color}
                gapSize={10}
                lineThickness={5}
                mediaStart={clipScene.mediaStart}
                mediaUrl={sceneAudioSrc(clipScene)}
                waveformUrl={clipScene.waveformUrl}
              />
            </div>
          </div>
        ) : (
          <div>
            <div className="flex h-[250px] flex-col justify-center px-[60px] text-[40px] leading-[170%] font-[500]">
              <p style={style.title} className="line-clamp-1 text-left">
                {clipScene.title}
              </p>

              <p style={style.subtitle} className="line-clamp-1 text-left">
                {clipScene.subtitle}
              </p>
            </div>

            <div
              className="flex h-[250px] items-center space-x-[50px] border-t-2 px-[60px]"
              style={{ borderColor: style.border.color }}
            >
              <FallbackImg
                className="aspect-square h-[130px] object-cover"
                src={clipScene.imageUrl}
              />

              <div className="h-full w-full">
                <BarsVisualization
                  className="h-full"
                  color={style.waveform.color}
                  gapSize={10}
                  lineThickness={5}
                  mediaStart={clipScene.mediaStart}
                  mediaUrl={sceneAudioSrc(clipScene)}
                  waveformUrl={clipScene.waveformUrl}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </AbsoluteFill>
  )
}

export const edgyLayoutInfo: LayoutInfo = {
  name: 'edgy',
  Component: Edgy,
  defaultScene: {
    ...defaultClipScene,
    layout: 'edgy',
  },
  defaultStyle: {
    background: { color: 'accent' },
    border: { color: 'primary' },
    logo: { color: 'secondary' },
    waveform: { color: 'primary' },
    subtitle: { color: 'primary', fontFamily: 'primary', opacity: 0.5 },
    title: {
      color: 'primary',
      fontFamily: 'primary',
    },
    speakerName: { color: 'secondary', fontFamily: 'primary' },
    speakerUsername: { color: 'secondary', fontFamily: 'primary' },
    transcription: {
      highlightColor: 'accent',
      color: 'secondary',
      fontFamily: 'primary',
      highlightType: 'all-words',
      fontSize: 70,
      scrollType: 'page-by-page',
      fontWeight: 500,
      linesPerPage: 3,
      lineHeight: 1.28,
      textAlign: 'left',
    },
  },
  transcriptions: {
    square: {},
    portrait: {
      fontSize: 110,
      linesPerPage: 9,
      lineHeight: 1,
    },
    landscape: {
      fontSize: 100,
      lineHeight: 1,
      linesPerPage: 7,
    },
  },
}
